import React, { FC, useContext, useState, useMemo } from "react";
import { demandWLGeneratorContext } from "./demandWLGeneratorContext";
import { 
    Grid, 
    DataGridPro, 
    MuiDataGrid, 
    Mui,
    AlertTypes,
    Button, 
    Switch, 
    FormControlLabel, 
    TextField, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Paper, 
    Box, 
    Typography,
    Delete as DeleteIcon,
    Edit as EditIcon,
    PlayArrow as PlayArrowIcon,
    Schedule as ScheduleIcon,
    Add as AddIcon,
} from "@leap4/frontend";
import { dateToStringFormat, formatDateDDMMYYYY } from "../../../utils/functions";
import ScheduleSettings from "./ScheduleSettings";
import {IDemandWLGenerator} from "../demandWLGenerator.interface";

type SelectChangeEvent<T = string> = Mui.SelectChangeEvent<T>;
type GridColDef<R = any> = MuiDataGrid.GridColDef<R>;

interface Filter {
    id: string;
    column: string;
    condition: string;
    value: string;
}

// Helper function to get cell value
const getCellValue = (template: IDemandWLGenerator, column: string): string => {
    switch (column) {
        case "templateName":
            return template.templateName || "";
        case "createBy":
            return template.createBy || "";
        case "created":
            return (template as any).created ? dateToStringFormat((template as any).created as string | number) : "";
        case "updated":
            return (template as any).updated ? dateToStringFormat((template as any).updated as string | number) : "";
        case "lastRunAt":
            return template.lastRunAt ? dateToStringFormat(template.lastRunAt as string | number) : "";
        case "runningDays":
            return template.scheduleReport?.runningDays?.join(", ") || "";
        case "emails":
            return template.scheduleReport?.emails?.join(", ") || "";
        default:
            return "";
    }
};

// Helper function to check if a template matches a filter
const matchesFilter = (template: IDemandWLGenerator, filter: Filter): boolean => {
    if (!filter.value.trim()) {
        return true; // Empty filter matches all
    }

    const cellValue = getCellValue(template, filter.column);
    const cellValueStr = String(cellValue).toLowerCase();
    const filterValueStr = filter.value.toLowerCase();

    switch (filter.condition) {
        case "contains":
            return cellValueStr.includes(filterValueStr);
        case "equals":
            return cellValueStr === filterValueStr;
        case "startsWith":
            return cellValueStr.startsWith(filterValueStr);
        case "endsWith":
            return cellValueStr.endsWith(filterValueStr);
        case "notContains":
            return !cellValueStr.includes(filterValueStr);
        default:
            return true;
    }
};

const SavedTemplates: FC = () => {
    const { runReportTemplate, deleteReportTemplate, updateReportTemplate, reportTemplates, saveOrUpdateWLGeneratorTemplate, setAlert } =
        useContext(demandWLGeneratorContext);

    const [openSchedule, setOpenSchedule] = useState<boolean>(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    
    const [filters, setFilters] = useState<Filter[]>([
        { id: "1", column: "templateName", condition: "contains", value: "" }
    ]);


    const handleOpenSchedule = (id: string) => {
        setSelectedTemplateId(id);
        setOpenSchedule(true);
    };

    const handleCloseSchedule = () => {
        setOpenSchedule(false);
        setSelectedTemplateId(null);
    };

    const handleScheduleUpdate = async (id: string, scheduleData: { isActive:boolean,runningDays: string[]; emails?: string[] }) => {
        saveOrUpdateWLGeneratorTemplate({ id: id, data: { scheduleReport: scheduleData }, ignoreValidation: true });
    };

    const handleDeleteTemplate = async (id: string) => {
        try {
            deleteReportTemplate(id);
            setAlert({ msg: "Template deleted successfully", type: AlertTypes.SUCCESS });
        } catch (error) {
            setAlert({ msg: "Error deleting template", type: AlertTypes.FAILURE });
        }
    };

    const handleToggleSchedulerActive = (id: string, currentValue: boolean) => {
        const template = reportTemplates.find(t => t._id === id);
        if (!template) return;
        
        const updatedScheduleData = {
            ...template.scheduleReport,
            isActive: !currentValue
        };
        
        saveOrUpdateWLGeneratorTemplate({ 
            id, 
            data: { scheduleReport: updatedScheduleData }, 
            ignoreValidation: true 
        });
        
        setAlert({ 
            msg: `Scheduler ${!currentValue ? 'activated' : 'deactivated'} successfully`, 
            type: AlertTypes.SUCCESS 
        });
    };

    // Filter logic - apply all filters (AND logic)
    const filteredTemplates = useMemo(() => {
        if (!reportTemplates || reportTemplates.length === 0) {
            return [];
        }
        
        // If no filters have values, return all templates
        const activeFilters = filters.filter(f => f.value.trim());
        if (activeFilters.length === 0) {
            return reportTemplates;
        }

        // Apply all filters (AND logic - template must match ALL filters)
        return reportTemplates.filter((template) => {
            return activeFilters.every(filter => matchesFilter(template, filter));
        });
    }, [reportTemplates, filters]);

    const handleAddFilter = () => {
        const newFilter: Filter = {
            id: Date.now().toString(),
            column: "templateName",
            condition: "contains",
            value: ""
        };
        setFilters([...filters, newFilter]);
    };

    const handleRemoveFilter = (filterId: string) => {
        if (filters.length > 1) {
            setFilters(filters.filter(f => f.id !== filterId));
        }
    };

    const handleFilterChange = (filterId: string, field: keyof Filter, value: string) => {
        setFilters(filters.map(f => 
            f.id === filterId ? { ...f, [field]: value } : f
        ));
    };

    const handleClearAllFilters = () => {
        setFilters([{ id: "1", column: "templateName", condition: "contains", value: "" }]);
    };

    const columns: GridColDef<IDemandWLGenerator>[] = [
        { field: "templateName", headerName: "Template Name", flex: 1 },
        { field: "createBy", headerName: "Creator Name", flex: 1 },
        {
            field: "created",
            headerName: "Created",
            flex: 1,
            type: 'dateTime',
            valueFormatter: (value: any) => {
                if (value && value instanceof Date) {
                    return formatDateDDMMYYYY(value);
                }
                return value ? dateToStringFormat(value as string | number) : 'N/A';
            },
        },
        {
            field: "updated",
            headerName: "Updated At",
            flex: 1,
            type: 'dateTime',
            valueFormatter: (value: any) => {
                if (value && value instanceof Date) {
                    return formatDateDDMMYYYY(value);
                }
                return value ? dateToStringFormat(value as string | number) : 'N/A';
            },
        },
        {
            field: "lastRunAt",
            headerName: "Last Run At",
            flex: 1,
            type: 'dateTime',
            valueFormatter: (value: any) => {
                if (value && value instanceof Date) {
                    return formatDateDDMMYYYY(value);
                }
                return value ? dateToStringFormat(value as string | number) : 'N/A';
            },
        },
        {
            field: "runningDays",
            headerName: "Running Days",
            flex: 1,
            valueGetter: (value, row) => row.scheduleReport?.runningDays?.join(", ") || "",
        },
        {
            field: "emails",
            headerName: "Emails",
            flex: 1,
            valueGetter: (value, row) => row.scheduleReport?.emails?.join(", ") || "",
        },
        {
            field: "schedulerActive",
            headerName: "Scheduler Active",
            flex: 1,
            renderCell: (params) => (
                <FormControlLabel
                    control={
                        <Switch
                            checked={params.row.scheduleReport?.isActive || false}
                            onChange={() => handleToggleSchedulerActive(
                                params.row._id, 
                                params.row.scheduleReport?.isActive || false
                            )}
                            disabled={!params.row.scheduleReport?.runningDays?.length}
                        />
                    }
                    label={params.row.scheduleReport?.isActive ? "Active" : "Inactive"}
                />
            ),
        },
        {
            field: "Remove",
            headerName: "Remove",
            sortable: false,
            minWidth: 90,
            renderCell: (params) => (
                <Button variant="contained" color="error" onClick={() => handleDeleteTemplate(params.row._id)}>
                    <DeleteIcon />
                </Button>
            ),
        },
        {
            field: "Modify",
            headerName: "Modify",
            sortable: false,
            minWidth: 90,
            renderCell: (params) => (
                <Button variant="contained" color="info" onClick={() => updateReportTemplate(params.row._id)}>
                    <EditIcon />
                </Button>
            ),
        },
        {
            field: "Run",
            headerName: "Run",
            sortable: false,
            minWidth: 90,
            renderCell: (params) => (
                <Button variant="contained" onClick={() => runReportTemplate(params.row._id)}>
                    <PlayArrowIcon />
                </Button>
            ),
        },
        {
            field: "Schedule",
            headerName: "Schedule",
            sortable: false,
            minWidth: 130,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ScheduleIcon />}
                    onClick={() => handleOpenSchedule(params.row._id)}
                >
                    Set
                </Button>
            ),
        },
    ];

    const filterableColumns = [
        { value: "templateName", label: "Template Name" },
        { value: "createBy", label: "Creator Name" },
        { value: "created", label: "Created" },
        { value: "updated", label: "Updated At" },
        { value: "lastRunAt", label: "Last Run At" },
        { value: "runningDays", label: "Running Days" },
        { value: "emails", label: "Emails" },
    ];

    const filterConditions = [
        { value: "contains", label: "Contains" },
        { value: "equals", label: "Equals" },
        { value: "startsWith", label: "Starts With" },
        { value: "endsWith", label: "Ends With" },
        { value: "notContains", label: "Does Not Contain" },
    ];

    return (
        <Box p={2}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Saved Templates</Typography>
            
            {/* Filter Box */}
            <Paper elevation={1} sx={{ padding: 2, marginBottom: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Filters</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddFilter}
                            sx={{ height: '40px' }}
                        >
                            Add Filter
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClearAllFilters}
                            sx={{ height: '40px' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                </Box>
                
                {filters.map((filter, index) => (
                    <Grid container spacing={2} alignItems="center" key={filter.id} sx={{ marginBottom: index < filters.length - 1 ? 2 : 0 }}>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Filter Column</InputLabel>
                                <Select
                                    value={filter.column}
                                    label="Filter Column"
                                    onChange={(e: SelectChangeEvent<string>) => handleFilterChange(filter.id, 'column', e.target.value)}
                                >
                                    {filterableColumns.map((col) => (
                                        <MenuItem key={col.value} value={col.value}>
                                            {col.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Condition</InputLabel>
                                <Select
                                    value={filter.condition}
                                    label="Condition"
                                    onChange={(e: SelectChangeEvent<string>) => handleFilterChange(filter.id, 'condition', e.target.value)}
                                >
                                    {filterConditions.map((cond) => (
                                        <MenuItem key={cond.value} value={cond.value}>
                                            {cond.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Filter Value"
                                value={filter.value}
                                onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                                placeholder="Enter filter value..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={2}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveFilter(filter.id)}
                                disabled={filters.length === 1}
                                sx={{ height: '40px', width: '100%' }}
                            >
                                Remove Filter
                            </Button>
                        </Grid>
                    </Grid>
                ))}
            </Paper>

            <Box sx={{ height: 800, width: "100%" }}>
                {/* TODO: FIX FOR VERSION 19.2.0 OF FRONTEND LIB - Remove disableColumnPinning once lib is updated */}
                <DataGridPro
                    disableColumnPinning={true}
                    rows={filteredTemplates}
                    columns={columns}
                    rowsPerPageOptions={[5, 10, 20, 100]}
                    loading={!reportTemplates}
                    getRowId={(row) => row._id}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'updated', sort: 'desc' }],
                        },
                    }}
                />
            </Box>

            {openSchedule && (
                <ScheduleSettings
                    open={openSchedule}
                    onClose={handleCloseSchedule}
                    templateId={selectedTemplateId}
                    onSave={handleScheduleUpdate}
                    data={reportTemplates.find((template) => template._id === selectedTemplateId)?.scheduleReport}
                />
            )}
        </Box>
    );
};

export default SavedTemplates;
